import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiHome, FiShield, FiKey, FiClipboard, FiLogOut, FiUsers, FiTrendingUp, FiServer, FiBook, FiMenu,
} from 'react-icons/fi';

import Dashboard from './components/Dashboard';
import EntraConfigs from './components/EntraConfigs';
import APIKeys from './components/APIKeys';
import AuditLog from './components/AuditLog';
import AdminUsers from './components/AdminUsers';
import LoginForm from './components/Login';
import UsageAnalytics from './components/UsageAnalytics';
import PostgresStatus from './components/PostgresStatus';
import ApiDocs from './components/ApiDocs';

const API_BASE = '';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'purple' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('sso_admin_token') || '');
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  const handleLogin = (t) => {
    setToken(t);
    localStorage.setItem('sso_admin_token', t);
    navigate('/');
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('sso_admin_token');
    navigate('/login');
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} apiBase={API_BASE} />;
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const renderSidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
          <img src="/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">SSO Entra</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={4} />

      {/* Navigation - Main */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
        Overview
      </Text>
      <NavButton to="/" icon={FiHome} isActive={location.pathname === '/'}>
        Dashboard
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        SSO
      </Text>
      <NavButton to="/entra-configs" icon={FiShield} isActive={isActive('/entra-configs')}>
        Entra Configs
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Configuration
      </Text>
      <NavButton to="/api-keys" icon={FiKey} isActive={isActive('/api-keys')}>
        API Keys
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Administration
      </Text>
      <NavButton to="/admin-users" icon={FiUsers} isActive={isActive('/admin-users')}>
        Admin Users
      </NavButton>
      <NavButton to="/audit-log" icon={FiClipboard} isActive={isActive('/audit-log')}>
        Audit Log
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Insights
      </Text>
      <NavButton to="/analytics" icon={FiTrendingUp} isActive={isActive('/analytics')}>
        Usage Analytics
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Infrastructure
      </Text>
      <NavButton to="/postgres" icon={FiServer} isActive={isActive('/postgres')}>
        PostgreSQL Status
      </NavButton>
      <NavButton to="/api-docs" icon={FiBook} isActive={isActive('/api-docs')}>
        API Docs
      </NavButton>

      <Spacer />

      {/* User Section */}
      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="purple.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        variant="ghost"
        colorScheme="whiteAlpha"
        justifyContent="flex-start"
        color="whiteAlpha.700"
        _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
        onClick={handleLogout}
      >
        Sign Out
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="purple.700"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">SSO Entra</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Sign out"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          py={6}
          px={4}
          bgGradient="linear(to-b, purple.600, purple.800)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        {/* Main Content */}
        <Box flex={1} overflowY="auto" p={{ base: 4, md: 8 }}>
          <Routes>
            <Route path="/" element={<Dashboard token={token} apiBase={API_BASE} />} />
            <Route path="/entra-configs/*" element={<EntraConfigs token={token} apiBase={API_BASE} />} />
            <Route path="/api-keys" element={<APIKeys token={token} apiBase={API_BASE} />} />
            <Route path="/audit-log" element={<AuditLog token={token} apiBase={API_BASE} />} />
            <Route path="/admin-users" element={<AdminUsers token={token} apiBase={API_BASE} />} />
            <Route path="/analytics" element={<UsageAnalytics />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/api-docs" element={<ApiDocs />} />
          </Routes>
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, purple.600, purple.800)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;
